import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './PhoneInputWithCountryCode.css';

const countryCodes = [
  { code: '+966', flag: '🇸🇦', name: 'Saudi Arabia', nameAr: 'السعودية' },
  { code: '+971', flag: '🇦🇪', name: 'UAE', nameAr: 'الإمارات' },
  { code: '+973', flag: '🇧🇭', name: 'Bahrain', nameAr: 'البحرين' },
  { code: '+974', flag: '🇶🇦', name: 'Qatar', nameAr: 'قطر' },
  { code: '+965', flag: '🇰🇼', name: 'Kuwait', nameAr: 'الكويت' },
  { code: '+968', flag: '🇴🇲', name: 'Oman', nameAr: 'عمان' },
  { code: '+962', flag: '🇯🇴', name: 'Jordan', nameAr: 'الأردن' },
  { code: '+961', flag: '🇱🇧', name: 'Lebanon', nameAr: 'لبنان' },
  { code: '+20', flag: '🇪🇬', name: 'Egypt', nameAr: 'مصر' },
  { code: '+212', flag: '🇲🇦', name: 'Morocco', nameAr: 'المغرب' },
  { code: '+216', flag: '🇹🇳', name: 'Tunisia', nameAr: 'تونس' },
  { code: '+213', flag: '🇩🇿', name: 'Algeria', nameAr: 'الجزائر' },
  { code: '+1', flag: '🇺🇸', name: 'USA', nameAr: 'الولايات المتحدة' },
  { code: '+44', flag: '🇬🇧', name: 'UK', nameAr: 'المملكة المتحدة' },
  { code: '+33', flag: '🇫🇷', name: 'France', nameAr: 'فرنسا' },
  { code: '+49', flag: '🇩🇪', name: 'Germany', nameAr: 'ألمانيا' },
  { code: '+91', flag: '🇮🇳', name: 'India', nameAr: 'الهند' },
  { code: '+86', flag: '🇨🇳', name: 'China', nameAr: 'الصين' },
  { code: '+81', flag: '🇯🇵', name: 'Japan', nameAr: 'اليابان' },
  { code: '+82', flag: '🇰🇷', name: 'South Korea', nameAr: 'كوريا الجنوبية' },
];

/**
 * Reusable phone input with country-code dropdown.
 *
 * Props:
 *   value               – controlled full phone string (e.g. "+96279123456")
 *   onChange(fullPhone) – callback whenever the number changes
 *   disabled            – boolean
 *   placeholder         – placeholder for the local-number input
 *   defaultCountryCode  – e.g. "+962"
 *   className           – extra classes for the wrapper
 */
const PhoneInputWithCountryCode = ({
  value = '',
  onChange,
  disabled = false,
  placeholder = '',
  defaultCountryCode = '+962',
  className = '',
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  const [selectedCode, setSelectedCode] = useState(defaultCountryCode);
  const [localPhone, setLocalPhone] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPos, setDropdownPos] = useState(null);

  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  /* Parse external value into country-code + local-number */
  useEffect(() => {
    if (!value) {
      setLocalPhone('');
      return;
    }
    const match = value.match(/^(\+\d{1,3})(.*)$/);
    if (match) {
      const [, code, rest] = match;
      const found = countryCodes.find((c) => c.code === code);
      if (found) {
        setSelectedCode(code);
        setLocalPhone(rest.replace(/\D/g, '').replace(/^0+/, ''));
        return;
      }
    }
    setLocalPhone(value.replace(/\D/g, '').replace(/^0+/, ''));
  }, [value]);

  /* Calculate dropdown position synchronously before paint */
  useLayoutEffect(() => {
    if (!isOpen) {
      setDropdownPos(null);
      return;
    }
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const width = Math.max(rect.width, 240);
      const dropdownHeight = 260; // approximate: search + max-height list
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
      setDropdownPos({
        top: openUp ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
        left: isRTL ? rect.right - width : rect.left,
        width,
      });
    }
  }, [isOpen, isRTL]);

  /* Close dropdown on click outside or resize */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isOpen) return;

      const inContainer = containerRef.current?.contains(event.target);
      const inDropdown = dropdownRef.current?.contains(event.target);

      /* Fallback for scrollbar clicks: check bounding rect */
      let inDropdownRect = false;
      if (dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect();
        inDropdownRect =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;
      }

      if (!inContainer && !inDropdown && !inDropdownRect) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    const handleClose = () => {
      setIsOpen(false);
      setSearchTerm('');
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleClose);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleClose);
    };
  }, [isOpen]);

  const emit = (code, local) => {
    if (!local.trim()) {
      onChange('');
      return;
    }
    const cleanedLocal = local.replace(/\D/g, '').replace(/^0+/, '');
    onChange(code + cleanedLocal);
  };

  const handleCountrySelect = (code) => {
    setSelectedCode(code);
    setIsOpen(false);
    setSearchTerm('');
    emit(code, localPhone);
  };

  const handleLocalChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, '');
    setLocalPhone(digitsOnly);
    emit(selectedCode, digitsOnly);
  };

  const filteredCountries = countryCodes.filter((c) => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return true;
    return (
      c.code.includes(term) || c.name.toLowerCase().includes(term)
    );
  });

  const selectedCountry = countryCodes.find((c) => c.code === selectedCode);

  return (
    <div
      className={`phone-input-group ${className}`}
      ref={containerRef}
    >
      <div
        ref={triggerRef}
        className="phone-country-select"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <span style={{ fontSize: '18px' }}>
          {selectedCountry?.flag || '🌍'}
        </span>
        <span style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
          {selectedCode}
        </span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="currentColor"
          style={{
            color: '#9CA3AF',
            marginInlineStart: 'auto',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        >
          <path d="m7 10 5 5 5-5z" />
        </svg>
      </div>

      <input
        type="tel"
        className="phone-local-input"
        value={localPhone}
        onChange={handleLocalChange}
        placeholder={placeholder}
        disabled={disabled}
      />

      {isOpen && dropdownPos &&
        createPortal(
          <div
            ref={dropdownRef}
            className="phone-country-dropdown"
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              zIndex: 10002,
              direction: 'ltr',
            }}
          >
            <div className="phone-dropdown-search">
              <input
                type="text"
                autoFocus
                placeholder={t('newPatientWizard.phoneInput.searchPlaceholder', 'Search country or code...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="phone-dropdown-list">
              {filteredCountries.map((country) => (
                <div
                  key={country.code}
                  className="phone-country-option"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCountrySelect(country.code);
                  }}
                >
                  <span style={{ fontSize: '18px' }}>{country.flag}</span>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151',
                    }}
                  >
                    {country.code}
                  </span>
                  <span
                    style={{
                      marginInlineStart: 'auto',
                      fontSize: '13px',
                      color: '#6B7280',
                    }}
                  >
                    {i18n.language === 'ar' && country.nameAr ? country.nameAr : country.name}
                  </span>
                </div>
              ))}
              {filteredCountries.length === 0 && (
                <div className="phone-dropdown-empty">
                  {t('newPatientWizard.phoneInput.noCountriesFound', 'No countries found')}
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default PhoneInputWithCountryCode;
